import { useEffect, useMemo } from "react"
import Link from "next/link"
import Image from "next/image"
import { useRouter } from "next/router"

import { Identity, OMUniverse } from "@apptivity-lab/firmament-node-sdk"
import { useMachine, useRestoredUniverse } from "@lib"

import { AffAttendeeProfile } from "@firmament-packages/aff"

import { AuthRegisterStateMachine } from "@machines/authentication/AuthRegisterStateMachine"

import RegisterForm from "@components/authentication/RegisterForm"
import AFFAuthPageLayout from "@components/domain/AFFAuthPageLayout"
import Modal from "@components/Modal"

const Content: React.FunctionComponent<{
    disabled?: boolean,
    error?: Error,
    identity?: Identity,
    attendeeProfile?: AffAttendeeProfile
    onSubmit: (identity: Identity, attendeeProfile: AffAttendeeProfile) => void
}> = ({ disabled, error, identity, attendeeProfile, onSubmit }) => {
    return (
        <>
            <h1 className="text-title1 md:text-large-title text-black-900">Register</h1>
            {/* TODO: Free trial days should follow registration package config. */}
            <p className="mt-[8px] text-subhead text-black-700">
                Register to get 14 days of free unlimited classes with expert instructors - no credit card required!
            </p>

            <RegisterForm className="mt-[32px]"
                disabled={disabled}
                error={error}
                identity={identity}
                attendeeProfile={attendeeProfile}
                onSubmit={onSubmit} />

            <hr className="border-black-200 mt-[32px]" />

            <div className="mt-[32px] mb-[72px] text-center">
                <p className="text-subhead text-black-700 mb-[8px]">Already have an account?</p>
                <Link href="/auth/login">
                    <a className="text-link uppercase">Log In To My Account</a>
                </Link>
            </div>
        </>
    )
}

function RegisterPage() {
    const router = useRouter()
    const [machine, machineState] = useMachine(AuthRegisterStateMachine)

    useRestoredUniverse(useMemo(() => () => {
        if (OMUniverse.shared.currentUser?.user.attendeeProfile()) {
            router.replace("/classes")
            return
        }

        machine.start()
    }, [machine, router]))

    switch (machineState.name) {
        case "showingError":
            return <Content
                error={machineState.error}
                identity={machineState.identity}
                attendeeProfile={machineState.attendeeProfile}
                onSubmit={(identity, attendeeProfile) => machine.showEmailConfirmation(identity, attendeeProfile)} />
        case "completed":
            router.replace(`/welcome`)

            // Show disabled form while redirecting
            return <Content
                disabled={true}
                identity={machineState.identity}
                attendeeProfile={machineState.attendeeProfile}
                onSubmit={(identity, attendeeProfile) => machine.showEmailConfirmation(identity, attendeeProfile)}
                />
        default:
            return (
                <>
                    <Modal show={machineState.name === "showingEmailConfirmation"} onDismiss={() => {
                        machine.back()
                    }}>
                        <div className="sm:px-[20px] sm:pb-[20px] flex flex-col align-middle  text-center">
                            <Image width="262" height="192" objectFit="contain" src="/images/ic_graphic_check_email.svg" alt="Register - Check Email" />
                            <h4 className="mt-[20px] text-title3 text-black-900">
                                Is your email correct?
                            </h4>

                            <p className="break-all mt-[8px] text-headline text-tertiary-900">
                                {machineState.name === "showingEmailConfirmation" ? machineState.identity.identifier : "-"}
                            </p>

                            <p className="mt-[24px] text-body text-black-700">
                                You will use your email to log in and receive class reminders.
                            </p>

                            <button type="button" className="mt-[32px] button-accent-filled w-full"
                                onClick={() => machineState.name === "showingEmailConfirmation" && machine.register(machineState.identity, machineState.attendeeProfile)}>
                                Yes, Correct
                            </button>

                            <button type="button" className="mt-[24px] link-text-primary"
                                onClick={() => machine.back()}>
                                No, Change Email
                            </button>
                        </div>
                    </Modal>

                    <Content disabled={machineState.name !== "presenting"}
                        identity={machineState.name === "presenting" || machineState.name === "showingEmailConfirmation" || machineState.name === "registering" ? machineState.identity : undefined}
                        attendeeProfile={machineState.name === "presenting" || machineState.name === "showingEmailConfirmation" || machineState.name === "registering" ? machineState.attendeeProfile : undefined}
                        onSubmit={(identity, attendeeProfile) => machine.showEmailConfirmation(identity, attendeeProfile)} />
                </>
            )
    }
}

RegisterPage.Layout = AFFAuthPageLayout

export default RegisterPage
